import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/components/ui/combobox";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useDebounce } from "@/shared/hooks/useDebounce";
import { useUsersByPermission } from "@/features/admin/hooks/useUser";
import {
  useAcknowledgeForecastRequest,
  useForecastRequests,
} from "@/features/production/hooks/useOrders";

const formatDateTime = (value?: string | null) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString();
};

const normalizeStatus = (value: string | null) => {
  if (value === "Pending" || value === "Acknowledged") return value;
  return "all";
};

const normalizeDate = (value: string | null) => {
  if (!value) return "";
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : "";
};

export default function ForecastRequestsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(() => searchParams.get("q") ?? "");
  const [status, setStatus] = useState(() => normalizeStatus(searchParams.get("status")));
  const [cropTypeId, setCropTypeId] = useState(() => searchParams.get("cropTypeId") || "all");
  const [agronomistUserId, setAgronomistUserId] = useState(() => searchParams.get("agronomistUserId") || "all");
  const [agronomistSearch, setAgronomistSearch] = useState("");
  const [fromDate, setFromDate] = useState(() => normalizeDate(searchParams.get("fromDate")));
  const [toDate, setToDate] = useState(() => normalizeDate(searchParams.get("toDate")));
  const debouncedSearch = useDebounce(search, 350);
  const agronomistsQuery = useUsersByPermission("agronomist.forecast_requests.create");
  const paramsString = searchParams.toString();

  useEffect(() => {
    const params = new URLSearchParams(paramsString);
    const nextSearch = params.get("q") ?? "";
    const nextStatus = normalizeStatus(params.get("status"));
    const nextCropTypeId = params.get("cropTypeId") || "all";
    const nextAgronomistUserId = params.get("agronomistUserId") || "all";
    const nextFromDate = normalizeDate(params.get("fromDate"));
    const nextToDate = normalizeDate(params.get("toDate"));

    setSearch((current) => (current === nextSearch ? current : nextSearch));
    setStatus((current) => (current === nextStatus ? current : nextStatus));
    setCropTypeId((current) => (current === nextCropTypeId ? current : nextCropTypeId));
    setAgronomistUserId((current) => (current === nextAgronomistUserId ? current : nextAgronomistUserId));
    setFromDate((current) => (current === nextFromDate ? current : nextFromDate));
    setToDate((current) => (current === nextToDate ? current : nextToDate));
  }, [paramsString]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (search) next.set("q", search);
    if (status !== "all") next.set("status", status);
    if (cropTypeId !== "all") next.set("cropTypeId", cropTypeId);
    if (agronomistUserId !== "all") next.set("agronomistUserId", agronomistUserId);
    if (fromDate) next.set("fromDate", fromDate);
    if (toDate) next.set("toDate", toDate);

    const nextString = next.toString();
    if (nextString !== paramsString) {
      setSearchParams(next, { replace: true });
    }
  }, [
    search,
    status,
    cropTypeId,
    agronomistUserId,
    fromDate,
    toDate,
    paramsString,
    setSearchParams,
  ]);

  const query = useForecastRequests({
    q: debouncedSearch.trim() || undefined,
    cropTypeId: cropTypeId === "all" ? undefined : cropTypeId,
    agronomistUserId: agronomistUserId === "all" ? undefined : agronomistUserId,
    status: status === "all" ? undefined : status,
    fromDate: fromDate || undefined,
    toDate: toDate || undefined,
  });

  const acknowledge = useAcknowledgeForecastRequest();

  const rows = useMemo(() => query.data ?? [], [query.data]);
  const cropOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of rows) {
      if (!map.has(row.cropTypeId)) map.set(row.cropTypeId, row.cropTypeName);
    }
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [rows]);

  const agronomistOptions = useMemo(() => {
    const map = new Map<string, string>();

    for (const user of agronomistsQuery.data ?? []) {
      const fullName = `${user.firstName || ""} ${user.otherNames || ""}`.trim();
      if (!fullName) continue;
      map.set(user.id, fullName);
    }

    // Keep a fallback from fetched rows for records tied to users not in the permission list.
    for (const row of rows) {
      if (!map.has(row.submittedByUserId)) {
        map.set(row.submittedByUserId, row.submittedByUserName || "-");
      }
    }

    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [agronomistsQuery.data, rows]);

  const filteredAgronomistOptions = useMemo(() => {
    const term = agronomistSearch.trim().toLowerCase();
    if (!term) return agronomistOptions;
    return agronomistOptions.filter((option) => option.name.toLowerCase().includes(term));
  }, [agronomistOptions, agronomistSearch]);

  useEffect(() => {
    if (agronomistUserId === "all") {
      setAgronomistSearch("");
      return;
    }

    const selected = agronomistOptions.find((option) => option.id === agronomistUserId);
    if (selected) {
      setAgronomistSearch(selected.name);
    }
  }, [agronomistUserId, agronomistOptions]);

  const clearFilters = () => {
    setSearch("");
    setStatus("all");
    setCropTypeId("all");
    setAgronomistUserId("all");
    setAgronomistSearch("");
    setFromDate("");
    setToDate("");
  };

  const onAcknowledge = async (requestId: string) => {
    try {
      await acknowledge.mutateAsync(requestId);
      toast.success("Forecast request acknowledged.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not acknowledge forecast request.");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Forecast Requests</h1>
          <p className="mt-1 text-muted-foreground">
            Agronomist demand signals awaiting sales acknowledgement.
          </p>
        </div>
        <Button variant="outline" onClick={() => void query.refetch()}>
          Refresh
        </Button>
      </div>

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-6">
        <Input
          placeholder="Search crop, variety, location, details"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="Pending">Pending</SelectItem>
            <SelectItem value="Acknowledged">Acknowledged</SelectItem>
          </SelectContent>
        </Select>
        <Select value={cropTypeId} onValueChange={setCropTypeId}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Crop type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All crops</SelectItem>
            {cropOptions.map((option) => (
              <SelectItem key={option.id} value={option.id}>{option.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Combobox
          value={agronomistUserId === "all" ? null : agronomistUserId}
          onValueChange={(value: string | null) => {
            if (!value) {
              setAgronomistUserId("all");
              setAgronomistSearch("");
              return;
            }

            setAgronomistUserId(value);
            const selected = agronomistOptions.find((option) => option.id === value);
            setAgronomistSearch(selected?.name ?? "");
          }}
          inputValue={agronomistSearch}
        >
          <ComboboxInput
            placeholder="All agronomists"
            className="w-full"
            showClear={agronomistUserId !== "all"}
            onChange={(event) => setAgronomistSearch(event.target.value)}
          />
          <ComboboxContent>
            <ComboboxList>
              {agronomistsQuery.isLoading && agronomistOptions.length === 0 ? (
                <ComboboxEmpty>Loading agronomists...</ComboboxEmpty>
              ) : filteredAgronomistOptions.length === 0 ? (
                <ComboboxEmpty>No agronomists found.</ComboboxEmpty>
              ) : (
                filteredAgronomistOptions.map((option) => (
                  <ComboboxItem key={option.id} value={option.id}>
                    {option.name}
                  </ComboboxItem>
                ))
              )}
            </ComboboxList>
          </ComboboxContent>
        </Combobox>
        <DatePicker
          value={fromDate}
          onChange={setFromDate}
          placeholder="From date"
        />
        <DatePicker
          value={toDate}
          onChange={setToDate}
          placeholder="To date"
          min={fromDate || undefined}
        />
      </div>
      <div className="flex justify-end">
        <Button variant="ghost" onClick={clearFilters}>Clear Filters</Button>
      </div>

      {query.isLoading ? (
        <div className="rounded-lg border bg-muted/40 p-6 text-sm text-muted-foreground">
          Loading forecast requests...
        </div>
      ) : query.error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-600">
          Failed to load forecast requests.
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border bg-muted/40 p-8 text-center text-muted-foreground">
          No forecast requests found.
        </div>
      ) : (
        <div className="rounded-lg border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Submitted</TableHead>
                <TableHead>Agronomist</TableHead>
                <TableHead>Crop / Variety</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Details</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const variety = row.cropVarietyName || row.freeTextVariety || "-";
                const isAcknowledged = row.status === "Acknowledged";
                const mapsUrl = `https://www.google.com/maps?q=${row.latitude},${row.longitude}`;

                return (
                  <TableRow key={row.id}>
                    <TableCell>{formatDateTime(row.createdAt)}</TableCell>
                    <TableCell>{row.submittedByUserName || "-"}</TableCell>
                    <TableCell>{row.cropTypeName} / {variety}</TableCell>
                    <TableCell>
                      <div className="space-y-0.5">
                        <p>{row.locationName}</p>
                        <p className="text-xs text-muted-foreground">
                          {row.latitude.toFixed(5)}, {row.longitude.toFixed(5)}
                        </p>
                        <a
                          href={mapsUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-sky-700 hover:underline"
                        >
                          Open map
                        </a>
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[360px]">
                      <p className="line-clamp-2" title={row.details}>{row.details}</p>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <Badge variant={isAcknowledged ? "default" : "secondary"}>{row.status}</Badge>
                        {isAcknowledged ? (
                          <p className="text-xs text-muted-foreground">{formatDateTime(row.acknowledgedAt)}</p>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      {isAcknowledged ? (
                        <Button size="sm" variant="outline" disabled>
                          Acknowledged
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => void onAcknowledge(row.id)}
                          disabled={acknowledge.isPending}
                        >
                          {acknowledge.isPending ? "Saving..." : "Acknowledge"}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
